import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Login from './pages/Login';
import Register from './pages/Register';
import Test from './pages/Test';
import Results from './pages/Results';

export const AuthContext = React.createContext();

function App() {
  const [authState, setAuthState] = useState({
    token: localStorage.getItem('token'),
    isAuthenticated: null,
    loading: true,
    user: null,
    hasCompletedTest: false
  });

  useEffect(() => {
    // Load user data if token exists
    if (localStorage.token) {
      loadUser();
    } else {
      setAuthState({
        ...authState,
        isAuthenticated: false,
        loading: false
      });
    }
  }, []);

  // Load user data
  const loadUser = async () => {
    if (localStorage.token) {
      setAuthToken(localStorage.token);
      
      try {
        const res = await axios.get('https://iq-test-theta.vercel.app/api/auth/user');
        
        setAuthState({
          ...authState,
          isAuthenticated: true,
          loading: false,
          user: res.data,
          hasCompletedTest: res.data.hasCompletedTest
        });
      } catch (err) {
        localStorage.removeItem('token');
        setAuthState({
          ...authState,
          token: null,
          isAuthenticated: false,
          loading: false
        });
      }
    }
  };

  // Set auth token for all axios requests
  const setAuthToken = token => {
    if (token) {
      axios.defaults.headers.common['x-auth-token'] = token;
    } else {
      delete axios.defaults.headers.common['x-auth-token'];
    }
  };

  const login = async (formData) => {
    try {
      const res = await axios.post('https://iq-test-theta.vercel.app/api/auth/login', formData);
      
      localStorage.setItem('token', res.data.token);
      
      setAuthState({
        ...authState,
        token: res.data.token,
        isAuthenticated: true,
        loading: false,
        hasCompletedTest: res.data.hasCompletedTest
      });
      
      loadUser();
      return true;
    } catch (err) {
      return {
        error: err.response.data.msg || 'Login failed. Please try again.'
      };
    }
  };

  const register = async (formData) => {
    try {
      const res = await axios.post('https://iq-test-theta.vercel.app/api/auth/register', formData);
      
      localStorage.setItem('token', res.data.token);
      
      setAuthState({
        ...authState,
        token: res.data.token,
        isAuthenticated: true,
        loading: false
      });
      
      loadUser();
      return true;
    } catch (err) {
      return {
        error: err.response.data.msg || 'Registration failed. Please try again.'
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setAuthState({
      ...authState,
      token: null,
      isAuthenticated: false,
      user: null,
      hasCompletedTest: false
    });
  };

  const setTestCompleted = () => {
    setAuthState({
      ...authState,
      hasCompletedTest: true
    });
  };

  return (
    <AuthContext.Provider
      value={{
        token: authState.token,
        isAuthenticated: authState.isAuthenticated,
        loading: authState.loading,
        user: authState.user,
        hasCompletedTest: authState.hasCompletedTest,
        login,
        register,
        logout,
        setTestCompleted
      }}
    >
      <Router>
        <Routes>
          <Route path="/login" element={
            !authState.isAuthenticated ? (
              <Login />
            ) : (
              authState.hasCompletedTest ? (
                <Navigate to="/results" />
              ) : (
                <Navigate to="/test" />
              )
            )
          } />
          <Route path="/register" element={
            !authState.isAuthenticated ? (
              <Register />
            ) : (
              authState.hasCompletedTest ? (
                <Navigate to="/results" />
              ) : (
                <Navigate to="/test" />
              )
            )
          } />
          <Route path="/test" element={
            authState.isAuthenticated ? (
              authState.hasCompletedTest ? (
                <Navigate to="/results" />
              ) : (
                <Test />
              )
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="/results" element={
            authState.isAuthenticated ? (
              authState.hasCompletedTest ? (
                <Results />
              ) : (
                <Navigate to="/test" />
              )
            ) : (
              <Navigate to="/login" />
            )
          } />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Router>
    </AuthContext.Provider>
  );
}

export default App;